import { useMemo } from 'react';

import { gql } from '@apollo/client';
import { useQuery, useApolloClient } from '@apollo/client/react';
import { css } from '@emotion/react';
import { Skeleton, Typography, Stack } from '@mui/material';
import { Box } from '@mui/system';
import { useTranslation, Types, RouteLink } from '@scorenco/core';

import { ClubLogo, Score } from '../../components';
import { T_KEYS } from '../../translations';

import { Card } from './_internal';
import { GetEventForEventHeroDocument } from './EventHero.query.generated';

const toDateInput = (v: unknown): string | number | Date =>
  typeof v === 'string' || typeof v === 'number' || v instanceof Date ? v : '';

type PlayingTeam = {
  score?: unknown;
  data?: {
    shootout?: unknown;
    sets?: unknown[];
    red_cards?: unknown;
    yellow_cards?: unknown;
    blue_cards?: unknown;
    white_cards?: unknown;
  };
  logo?: string;
  name?: string;
  name_in_competition?: string;
  team_id?: number;
  role?: string;
};

const normalizeLive = (live?: Types.Lives_Live) => {
  if (!live) {
    return {
      halfTime: undefined,
    };
  }

  return (live.posts || ([] as Types.Lives_Post[])).reduce(
    (acc, post) => {
      if (
        post.action?.action_type === 'half' &&
        (post.data as { end?: unknown } | null | undefined)?.end
      ) {
        return {
          ...acc,
          halfTime: post,
        };
      }

      return acc;
    },
    {
      halfTime: undefined as Types.Lives_Post | undefined,
    }
  );
};

const TeamName = ({ name, ...rest }: { name: string }) => (
  <Typography
    css={css`
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      flex: 1;
      overflow: hidden;
      line-height: 1.2;
      text-transform: uppercase;
      font-size: 0.7rem;
      white-space: normal;
      word-wrap: break-word;
    `}
    variant="h5"
    {...rest}
  >
    {name || <Skeleton />}
  </Typography>
);

const EventStatus = ({ status, ...rest }: { status?: string }) => {
  const { t } = useTranslation();
  if (status === 'finish' || status === 'postponed') {
    return (
      <Typography {...rest} sx={{ color: 'white' }}>
        {status === 'finish' && t(T_KEYS.EVENT_FINISH)}
        {status === 'postponed' && t(T_KEYS.EVENT_POSTPONED)}
      </Typography>
    );
  } else {
    return null;
  }
};

export const EventHero = ({
  event,
  live,
  ...rest
}: {
  event?: Types.Competitions_Event_Detail;
  live?: Types.Lives_Live;
}) => {
  const { t } = useTranslation();
  const eventQuery = useQuery(GetEventForEventHeroDocument, {
    variables: {
      eventId: event?.id as number,
    },
    context: { role: 'anonymous' },
    skip: !event?.id,
  });

  // On vérifie si le match est la démo en vérifiant le cache
  const client = useApolloClient();
  const demo = client.readFragment<{ id: number; demo: boolean }>({
    id: `competitions_event:${event?.id}`,
    fragment: gql`
      fragment Event on competitions_event {
        id
        demo
      }
    `,
  })?.demo;

  const e = eventQuery.data?.competitions_event_detail_by_id?.[0] || event;
  const eTeams = e?.teams as PlayingTeam[] | undefined;
  const eventTeams = event?.teams as PlayingTeam[] | undefined;
  const home_score = eTeams?.[0]?.score as number | undefined;
  const away_score = eTeams?.[1]?.score as number | undefined;
  const home_shootout = eTeams?.[0]?.data?.shootout as number | undefined;
  const away_shootout = eTeams?.[1]?.data?.shootout as number | undefined;
  const l = useMemo(() => normalizeLive(live), [live]);
  const isStarted =
    (eventTeams || []).reduce(
      (acc: boolean, team: PlayingTeam) =>
        acc && team?.score !== null && team?.score !== undefined,
      true
    ) ?? false;

  let scoreboard;
  if (e?.status === 'to_come') {
    scoreboard = (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
        }}
      >
        <Typography
          sx={{
            fontSize: '1.2rem',
            fontWeight: 800,
            lineHeight: 1,
            textAlign: 'center',
          }}
          variant="body1"
          component="span"
        >
          {(!!e?.date &&
            new Date(toDateInput(e.date)).toLocaleDateString([], {
              month: 'short',
              day: '2-digit',
            })) || <Skeleton />}
        </Typography>
        <Typography
          variant="body1"
          component="span"
          sx={{ textAlign: 'center' }}
        >
          {(!!e?.time &&
            new Date(toDateInput(e.time)).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })) || <Skeleton />}
        </Typography>
      </Box>
    );
  } else if (e?.status === 'live' && !isStarted) {
    scoreboard = (
      <Typography
        variant="body1"
        sx={{
          fontStyle: 'italic',
          textAlign: 'center',
          lineHeight: 1.2,
          color: 'white',
        }}
        component="span"
        dangerouslySetInnerHTML={{ __html: t(T_KEYS.EVENT_CARD_GIVE_SCORE) }}
      />
    );
  } else {
    scoreboard = (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              marginRight: '1.5rem',
              width: '2rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
            }}
          >
            {eTeams?.[0]?.data?.sets?.map((_: unknown, index: number) => {
              return (
                <Score
                  key={index}
                  score={
                    (eTeams?.[0]?.data?.sets as number[] | undefined)?.[
                      index
                    ] ?? 0
                  }
                  opponentScore={
                    (eTeams?.[1]?.data?.sets as number[] | undefined)?.[
                      index
                    ] ?? 0
                  }
                  isWinner={
                    ((eTeams?.[0]?.data?.sets as number[] | undefined)?.[
                      index
                    ] ?? 0) >
                    ((eTeams?.[1]?.data?.sets as number[] | undefined)?.[
                      index
                    ] ?? 0)
                  }
                  sx={{ width: '1.1rem', height: '1.5rem', mb: 1 }}
                />
              );
            })}
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              width: { xs: '30%', sm: '30%' },
              margin: '0 0.5rem',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
              }}
            >
              <Score
                score={home_score ?? 0}
                opponentScore={away_score ?? 0}
                isWinner={
                  e?.status === 'finish' &&
                  (home_score ?? 0) > (away_score ?? 0)
                }
                sx={{
                  width: { xs: '2.8rem', sm: '4rem' },
                  height: { xs: '3.5rem', sm: '4.5rem' },
                  fontSize: { xs: '2.2rem', sm: '3rem' },
                }}
              />
              <Score
                score={away_score ?? 0}
                opponentScore={home_score ?? 0}
                isWinner={
                  e?.status === 'finish' &&
                  (home_score ?? 0) < (away_score ?? 0)
                }
                sx={{
                  width: { xs: '2.8rem', sm: '4rem' },
                  height: { xs: '3.5rem', sm: '4.5rem' },
                  fontSize: { xs: '2.2rem', sm: '3rem' },
                }}
              />
            </Box>
          </Box>
          <Box
            sx={{
              marginLeft: '1.5rem',
              width: '2rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
            }}
          >
            {eTeams?.[1]?.data?.sets?.map((_: unknown, index: number) => {
              return (
                <Score
                  key={index}
                  score={
                    (eTeams?.[1]?.data?.sets as number[] | undefined)?.[
                      index
                    ] ?? 0
                  }
                  opponentScore={
                    (eTeams?.[0]?.data?.sets as number[] | undefined)?.[
                      index
                    ] ?? 0
                  }
                  isWinner={
                    ((eTeams?.[1]?.data?.sets as number[] | undefined)?.[
                      index
                    ] ?? 0) >
                    ((eTeams?.[0]?.data?.sets as number[] | undefined)?.[
                      index
                    ] ?? 0)
                  }
                  sx={{ width: '1.1rem', height: '1.5rem', mb: 1 }}
                />
              );
            })}
          </Box>
        </Box>
        {live && l.halfTime && (
          <Box
            sx={{
              marginTop: '0.75rem',
            }}
          >
            <Typography
              sx={{
                lineHeight: 1,
                m: 0,
                textAlign: 'center',
                fontWeight: 800,
              }}
              variant="body2"
              component="span"
            >
              {
                (
                  l.halfTime?.data as
                    | { main_score_1?: unknown }
                    | null
                    | undefined
                )?.main_score_1 as number | undefined
              }
            </Typography>
            <Typography
              sx={{
                lineHeight: 1,
                m: 0,
                mr: 1,
                ml: 1,
                fontSize: ' 0.7rem',
                textAlign: 'center',
              }}
              variant="body2"
              component="span"
            >
              {t(T_KEYS.EVENT_HALFTIME)}
            </Typography>
            <Typography
              sx={{
                lineHeight: 1,
                m: 0,
                textAlign: 'center',
                fontWeight: 800,
              }}
              variant="body2"
              component="span"
            >
              {
                (
                  l.halfTime?.data as
                    | { main_score_2?: unknown }
                    | null
                    | undefined
                )?.main_score_2 as number | undefined
              }
            </Typography>
          </Box>
        )}
        {/* TODO: Afficher les quart temps lorsque le basket sera géré dans la
        nouvelle version */}
        {/* {e?.status === 'finish' && (
          <Box
            sx={{
              marginTop: '0.75rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 1,
                ml: 1,
              }}
            >
              <Typography
                sx={{
                  lineHeight: 1,
                  m: 0,
                  mb: 0.5,
                  fontSize: ' 0.7rem',
                  textAlign: 'center',
                }}
                variant="body2"
                component="span"
              >
                QT1
              </Typography>
              <Typography
                sx={{
                  lineHeight: 1,
                  m: 0,
                  textAlign: 'center',
                  fontWeight: 800,
                }}
                variant="body2"
                component="span"
              >
                24 - 16
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 1,
                ml: 1,
              }}
            >
              <Typography
                sx={{
                  lineHeight: 1,
                  m: 0,
                  mb: 0.5,
                  fontSize: ' 0.7rem',
                  textAlign: 'center',
                }}
                variant="body2"
                component="span"
              >
                QT2
              </Typography>
              <Typography
                sx={{
                  lineHeight: 1,
                  m: 0,
                  textAlign: 'center',
                  fontWeight: 800,
                }}
                variant="body2"
                component="span"
              >
                21 - 16
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 1,
                ml: 1,
              }}
            >
              <Typography
                sx={{
                  lineHeight: 1,
                  m: 0,
                  mb: 0.5,
                  fontSize: ' 0.7rem',
                  textAlign: 'center',
                }}
                variant="body2"
                component="span"
              >
                QT3
              </Typography>
              <Typography
                sx={{
                  lineHeight: 1,
                  m: 0,
                  textAlign: 'center',
                  fontWeight: 800,
                }}
                variant="body2"
                component="span"
              >
                29 - 17
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 1,
                ml: 1,
              }}
            >
              <Typography
                sx={{
                  lineHeight: 1,
                  m: 0,
                  mb: 0.5,
                  fontSize: ' 0.7rem',
                  textAlign: 'center',
                }}
                variant="body2"
                component="span"
              >
                QT4
              </Typography>
              <Typography
                sx={{
                  lineHeight: 1,
                  m: 0,
                  textAlign: 'center',
                  fontWeight: 800,
                }}
                variant="body2"
                component="span"
              >
                19 - 14
              </Typography>
            </Box>
          </Box>
        )} */}
        {((home_shootout ?? 0) > 0 || (away_shootout ?? 0) > 0) && (
          <Box
            sx={{
              mt: 1.5,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Typography
              sx={{
                lineHeight: 1,
                m: 0,
                fontWeight: 800,
                color: (theme) => theme.vars.palette.background.default,
              }}
              variant="body2"
              component="span"
            >
              {t(T_KEYS.ACTION_SHOOTOUT)}
            </Typography>
            <Box
              sx={{
                mt: 1,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                color: (theme) => theme.palette.background.default,
              }}
            >
              <Score
                score={home_shootout ?? 0}
                opponentScore={away_shootout ?? 0}
                isWinner={(home_shootout ?? 0) > (away_shootout ?? 0)}
                sx={{ width: '1.5rem', height: '2rem', mr: '0.25rem' }}
              />
              -
              <Score
                score={away_shootout ?? 0}
                opponentScore={home_shootout ?? 0}
                isWinner={(away_shootout ?? 0) > (home_shootout ?? 0)}
                sx={{ width: '1.5rem', height: '2rem', ml: '0.25rem' }}
              />
            </Box>
          </Box>
        )}

        {e?.status === 'live' && (
          <Box
            sx={{
              background: '#e57373',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              paddingTop: '0.3rem',
              paddingBottom: '0.3rem',
              marginTop: '0.75rem',
              borderRadius: '0.25rem',
              width: 'calc(100% - 1rem)',
            }}
          >
            <Typography
              sx={{
                lineHeight: 1,
                m: 0,
                fontWeight: 800,
                color: 'white',
                textTransform: 'uppercase',
              }}
              variant="body2"
              component="span"
              color="white"
            >
              {t(T_KEYS.EVENT_LIVE)}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Stack spacing={1} {...rest}>
      <Stack
        direction="row"
        justifyContent="center"
        css={css`
          width: 100%;
        `}
      >
        <EventStatus status={e?.status} />
      </Stack>
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        {/* Première ligne : Logos et Score */}
        <Stack
          direction="row"
          css={css`
            width: 100%;
            align-items: center;
            justify-content: space-between;
          `}
        >
          {/* Équipe domicile */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              width: '25%',
            }}
          >
            {live && (
              <Box
                sx={{
                  marginRight: '0.5rem',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Card
                  actionType="red_card"
                  count={(eTeams?.[0]?.data?.red_cards as number) ?? 0}
                />
                <Card
                  actionType="yellow_card"
                  count={(eTeams?.[0]?.data?.yellow_cards as number) ?? 0}
                />
                <Card
                  actionType="blue_card"
                  count={(eTeams?.[0]?.data?.blue_cards as number) ?? 0}
                />
                <Card
                  actionType="white_card"
                  count={(eTeams?.[0]?.data?.white_cards as number) ?? 0}
                />
              </Box>
            )}
            <Box
              component={RouteLink}
              href={{
                routeKey: 'Team',
                params: { teamId: String(eTeams?.[0]?.team_id) },
              }}
              disabled={demo || !eTeams?.[0]?.team_id}
            >
              <ClubLogo
                css={css`
                  height: 3.5rem;
                  object-position: left;
                  cursor: pointer;
                `}
                logo={eTeams?.[0]?.logo}
                name={eTeams?.[0]?.name_in_competition}
                shieldColor="home"
                width={200}
                height={200}
              />
            </Box>
          </Box>

          {/* Score central */}
          {scoreboard}

          {/* Équipe extérieur */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              width: '25%',
              justifyContent: 'flex-end',
            }}
          >
            <Box
              component={RouteLink}
              href={{
                routeKey: 'Team',
                params: { teamId: String(eTeams?.[1]?.team_id) },
              }}
              disabled={demo || !eTeams?.[1]?.team_id}
            >
              <ClubLogo
                css={css`
                  height: 3.5rem;
                  object-position: right;
                  cursor: pointer;
                `}
                logo={eTeams?.[1]?.logo}
                name={eTeams?.[1]?.name_in_competition}
                shieldColor="away"
                width={200}
                height={200}
              />
            </Box>
            {live && (
              <Box
                sx={{
                  marginLeft: '0.5rem',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Card
                  actionType="red_card"
                  count={(eTeams?.[1]?.data?.red_cards as number) ?? 0}
                />
                <Card
                  actionType="yellow_card"
                  count={(eTeams?.[1]?.data?.yellow_cards as number) ?? 0}
                />
                <Card
                  actionType="blue_card"
                  count={(eTeams?.[1]?.data?.blue_cards as number) ?? 0}
                />
                <Card
                  actionType="white_card"
                  count={(eTeams?.[1]?.data?.white_cards as number) ?? 0}
                />
              </Box>
            )}
          </Box>
        </Stack>

        {/* Deuxième ligne : Noms des équipes */}
        <Stack
          direction="row"
          sx={{
            width: '100%',
            justifyContent: 'space-between',
            mt: 1,

            minHeight: '2.5rem',
          }}
        >
          {eTeams?.map(
            (
              team: { team_id?: number; name_in_competition?: string },
              idx: number
            ) => (
              <Box
                key={idx}
                component={RouteLink}
                href={{
                  routeKey: 'Team',
                  params: { teamId: String(team?.team_id) },
                }}
                disabled={demo || !team?.team_id}
              >
                <TeamName
                  name={team?.name_in_competition ?? ''}
                  css={css`
                    text-align: ${idx === 0 ? 'left' : 'right'};
                    color: white;
                    width: 45%;
                    cursor: pointer;
                  `}
                />
              </Box>
            )
          )}
        </Stack>
      </Box>
    </Stack>
  );
};
